import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { ConfirmDialog } from "@/client/components/confirm-dialog";
import { CopyButton } from "@/client/components/copy-button";
import { PageHeader } from "@/client/components/page-header";
import { ProvisioningNote } from "@/client/components/provisioning-note";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/client/components/ui/form";
import { Input } from "@/client/components/ui/input";
import { Skeleton } from "@/client/components/ui/skeleton";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { GuideLink } from "@/client/features/docs/placements";
import { type SshKey, sshKeysApi } from "@/client/features/ssh-keys/ssh-keys-api";
import { ApiError } from "@/client/lib/api";

const textareaClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 min-h-24 w-full rounded-md border px-3 py-2 font-mono text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

const addKeySchema = z.object({
  publicKey: z.string().min(1, "Paste an SSH public key."),
  label: z.string().max(100, "Keep the label under 100 characters.").optional(),
});

type AddKeyValues = z.infer<typeof addKeySchema>;

function AddKeyForm({ onAdded }: { onAdded: () => void }) {
  const deactivated = useAuthStore((s) => s.deactivated);
  const form = useForm<AddKeyValues>({
    resolver: zodResolver(addKeySchema),
    defaultValues: { publicKey: "", label: "" },
  });

  const onSubmit = async (values: AddKeyValues) => {
    try {
      await sshKeysApi.create({
        publicKey: values.publicKey,
        label: values.label?.trim() || undefined,
      });
      toast.success("SSH key added.");
      form.reset({ publicKey: "", label: "" });
      onAdded();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : "Unable to add the key. Please try again.";
      toast.error(message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add an SSH key</CardTitle>
        <CardDescription>
          Paste an ed25519, RSA, or ECDSA public key. ed25519 is recommended - generate one and copy
          your public key:
        </CardDescription>
      </CardHeader>
      <CardContent>
        <pre className="bg-muted text-muted-foreground mb-4 overflow-x-auto rounded-md p-3 font-mono text-xs">
          <code>
            {
              'ssh-keygen -t ed25519 -C "you@example.com"\ncat ~/.ssh/id_ed25519.pub   # paste this line'
            }
          </code>
        </pre>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-4">
            <FormField
              control={form.control}
              name="publicKey"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Public key</FormLabel>
                  <FormControl>
                    <textarea
                      className={textareaClass}
                      placeholder="ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAA... you@example.com"
                      spellCheck={false}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    Paste the contents of your <code>.pub</code> file.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem className="sm:max-w-xs">
                  <FormLabel>Label (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Work laptop" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-fit"
              disabled={form.formState.isSubmitting || deactivated}
            >
              {form.formState.isSubmitting ? "Adding…" : "Add key"}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}

function DeleteKeyDialog({ keyItem, onDeleted }: { keyItem: SshKey; onDeleted: () => void }) {
  const deactivated = useAuthStore((s) => s.deactivated);
  return (
    <ConfirmDialog
      trigger={
        <Button variant="outline" size="sm" disabled={deactivated}>
          Remove
        </Button>
      }
      title="Remove SSH key?"
      description={
        <>
          {keyItem.label ? `"${keyItem.label}" ` : ""}This can't be undone. Anything relying on this
          key will lose access once provisioning updates.
        </>
      }
      confirmLabel="Remove key"
      pendingLabel="Removing…"
      errorMessage="Unable to remove the key. Please try again."
      onConfirm={async () => {
        await sshKeysApi.remove(keyItem.id);
        toast.success("SSH key removed.");
        onDeleted();
      }}
    >
      <p className="text-muted-foreground font-mono text-xs break-all">{keyItem.fingerprint}</p>
    </ConfirmDialog>
  );
}

function KeysTable({
  keys,
  onChanged,
  canManageStatus,
}: {
  keys: SshKey[];
  onChanged: () => void;
  canManageStatus: boolean;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-muted-foreground border-b text-left">
            <th className="py-2 pr-4 font-medium">Label</th>
            <th className="py-2 pr-4 font-medium">Public key</th>
            {/* The staff view-as listing spans the whole tenant, so attribute keys. */}
            {canManageStatus ? <th className="py-2 pr-4 font-medium">Owner</th> : null}
            <th className="py-2 pr-4 font-medium">Added</th>
            <th className="py-2 font-medium" />
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => (
            <tr key={k.id} className="border-b last:border-0">
              <td className="py-2 pr-4 align-middle">{k.label ?? k.comment ?? "—"}</td>
              <td className="py-2 pr-4 align-middle">
                <div className="flex min-w-0 items-center gap-1.5">
                  <span
                    className="block max-w-[32rem] truncate font-mono text-xs"
                    title={k.publicKey}
                  >
                    {k.publicKey}
                  </span>
                  <CopyButton value={k.publicKey} label="Copy public key" />
                </div>
              </td>
              {canManageStatus ? (
                <td className="text-muted-foreground py-2 pr-4 align-middle">
                  {k.ownerName ?? "—"}
                </td>
              ) : null}
              <td className="text-muted-foreground py-2 pr-4 align-middle whitespace-nowrap">
                {new Date(k.createdAt).toLocaleDateString()}
              </td>
              <td className="py-2 text-right align-middle">
                {/* Deletes are blocked while viewing as a tenant (read-only), and
                    the staff listing spans keys staff don't own - so hide it. */}
                {canManageStatus ? null : <DeleteKeyDialog keyItem={k} onDeleted={onChanged} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SshKeysPage() {
  // Staff viewing the tenant ("view as") get the whole-tenant listing (owner
  // column, no per-key delete). An active impersonation is always a staff admin.
  const canManageStatus = useAuthStore((s) => s.impersonation != null);
  const [keys, setKeys] = useState<SshKey[] | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await sshKeysApi.list();
      setKeys(res.keys);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(err instanceof ApiError ? err.message : "Failed to load your SSH keys.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="SSH keys"
        description="Add and manage the SSH public keys used to access your resources."
      >
        <GuideLink slot="ssh-keys.help" />
      </PageHeader>

      <AddKeyForm onAdded={refresh} />

      <Card>
        <CardHeader>
          <CardTitle>Your keys</CardTitle>
          <CardDescription>Public keys you've added to your account.</CardDescription>
          <ProvisioningNote className="pt-1" />
        </CardHeader>
        <CardContent>
          {/* Prefer showing whatever list we have: a failed refresh (e.g. after
              a successful add/delete) shouldn't wipe a roster we already loaded.
              The toast already surfaces the refresh failure. */}
          {loading ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : keys && keys.length > 0 ? (
            <KeysTable keys={keys} onChanged={refresh} canManageStatus={canManageStatus} />
          ) : error ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load your SSH keys. Refresh to try again.
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">No SSH keys yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
